import type {
  MCDOffer,
  MCDDeals,
  MCDUser,
  MCDAuth,
  MCDLanguage,
  MCDCountry,
  MCDVersion,
  MCDTagsData,
  MCDLocationData,
  MCDOffersData
} from './types';

import { isDEV } from '@/utils/Location';
export const App = window as Application;
const params = new URLSearchParams(location.search);

export const openHome = () => (location.href = 'gmalite://gmalite-home');
export const openDeals = () => (location.href = 'gmalite://gmalite-deals');
export const openAccount = () => (location.href = 'gmalite://gmalite-account');

export const activateOffer = (loyalty: number, reward: number) =>
  isDEV()
    ? new Promise(resolve => resolve(undefined))
    : new Promise((resolve, reject) => {
        const offer = App.mcd.bridge.message('offerActivation') as MCDOffer;

        offer.send({
          autoActivate: false,
          loyaltyId: loyalty,
          rewardId: reward
        });

        offer.on('error', error => reject(error));
        offer.on('data', data => resolve(data));
      });

export const orientationScreen = (landscape = false) =>
  !isDEV() &&
  (App.mcd.bridge.message('system') as MCDUser).send({
    appOrientation: (landscape && 'landscape') || 'portrait'
  });

export const burnUserPoints = (quantity: number) =>
  isDEV()
    ? new Promise(resolve => resolve(undefined))
    : new Promise((resolve, reject) => {
        const deals = App.mcd.bridge.message('deals') as MCDDeals;
        deals.send({ burnPoints: true, points: -quantity });

        deals.on('data', data => resolve(data.points));
        deals.on('error', error => reject(error));
      });

export const addPlexureTags = (tags: string[]) =>
  isDEV()
    ? new Promise(resolve => resolve(undefined))
    : new Promise((resolve, reject) => {
        const user = App.mcd.bridge.message('user') as MCDUser;
        user.send({ addTags: tags });

        user.on('error', error => reject(error));
        user.on('data', data => resolve(data));
      });

export const getAuthCode = (clientId: string) =>
  isDEV()
    ? new Promise<undefined>(resolve => resolve(void 0))
    : new Promise<string>((resolve, reject) => {
        const user = App.mcd.bridge.message('user') as MCDAuth;
        user.send({ getAuthCode: true, clientId });

        user.on('data', data => resolve(data.authCode));
        user.on('error', error => reject(error));
      });

export const toggleFullscreen = () => {
  const system = App.mcd.bridge.message('system');
  system.send({ hideCloseButton: true, fullscreen: true });
};

export const getUserLanguage = () =>
  isDEV()
    ? new Promise<string>(resolve =>
        resolve(
          (params.get('lang') ?? import.meta.env.VITE_USER_LANGUAGE) || 'en'
        )
      )
    : new Promise<string>((resolve, reject) => {
        const system = App.mcd.bridge.message('system') as MCDLanguage;
        system.send({ getSelectedLanguage: true });

        system.on('data', data => resolve(data.language));
        system.on('error', error => reject(error));
      });

export const getUserCountry = () =>
  isDEV()
    ? new Promise<string>(resolve =>
        resolve(
          (params.get('country') ?? import.meta.env.VITE_USER_COUNTRY) || 'nl'
        )
      )
    : new Promise<string>((resolve, reject) => {
        const system = App.mcd.bridge.message('system') as MCDCountry;
        system.send({ getMarketId: true });

        system.on('data', data => resolve(data.marketId));
        system.on('error', error => reject(error));
      });

export const getUserPoints = () =>
  isDEV()
    ? new Promise<number>(resolve => resolve(Infinity))
    : new Promise<number>((resolve, reject) => {
        const deals = App.mcd.bridge.message('deals') as MCDDeals;
        deals.send({ getPoints: true });

        deals.on('data', data => resolve(data.points));
        deals.on('error', error => reject(error));
      });

export const getUserData = () =>
  isDEV()
    ? new Promise(resolve =>
        resolve({
          firstname: params.get('firstname') ?? 'John',
          lastname: params.get('lastname') ?? 'Carmack',
          email: params.get('email') ?? 'carmack@idsoftware.com',
          deviceId: params.get('deviceId') ?? 'bfg_666'
        })
      )
    : new Promise((resolve, reject) => {
        const user = App.mcd.bridge.message('user') as MCDUser;
        user.send({ getuser: true, promptlogin: true });

        user.on('error', error => reject(error));
        user.on('data', data => resolve(data));
      });

export const promptLogin = () =>
  isDEV()
    ? new Promise(resolve => resolve(undefined))
    : new Promise((resolve, reject) => {
        const user = App.mcd.bridge.message('user') as MCDUser;
        user.send({ promptlogin: true });

        user.on('error', error => reject(error));
        user.on('data', data => resolve(data));
      });

export const getAppVersion = () =>
  isDEV()
    ? new Promise<string>(resolve => resolve('3.16.035938.preview.35938'))
    : new Promise<string>((resolve, reject) => {
        const system = App.mcd.bridge.message('system') as MCDVersion;
        system.send({ getVersion: true });

        system.on('data', data => resolve(data.version));
        system.on('error', error => reject(error));
      });

export const getPlexureTags = () =>
  isDEV()
    ? new Promise<Partial<MCDTagsData>>(resolve => resolve({}))
    : new Promise<MCDTagsData>((resolve, reject) => {
        const user = App.mcd.bridge.message('user') as MCDUser<MCDTagsData>;
        user.send({ getTags: true });

        user.on('error', error => reject(error));
        user.on('data', data => resolve(data));
      });

export const getUserLocation = () =>
  isDEV()
    ? new Promise<MCDLocationData>(resolve =>
        resolve({
          longitude: 0,
          latitude: 0,
          speed: 0,
          time: 0
        })
      )
    : new Promise<MCDLocationData>((resolve, reject) => {
        const location = App.mcd.bridge.message(
          'location'
        ) as MCDUser<MCDLocationData>;

        location.send({ continuous: false });

        location.on('done', () => console.info('"getUserLocation" Done!'));
        location.on('error', error => reject(error));
        location.on('data', data => resolve(data));
      });

export const getRedeemedOffers = () =>
  isDEV()
    ? new Promise<Partial<MCDOffersData>>(resolve => resolve({}))
    : new Promise<MCDOffersData>((resolve, reject) => {
        const user = App.mcd.bridge.message('offers') as MCDUser<MCDOffersData>;
        user.send({ getRedeemedOffers: true });

        user.on('error', error => reject(error));
        user.on('data', data => resolve(data));
      });
